// Shared dashboard helpers — formatters, parsers, color palettes.
// Pure functions, no React, easy to reuse across dashboard sub-components.

export const STAGE_COLORS = {
  "PO Received":     { color: "#3b82f6", bg: "rgba(59,130,246,0.07)" },
  "Procurement":     { color: "#f97316", bg: "rgba(249,115,22,0.07)" },
  "Manufacturing":   { color: "#a855f7", bg: "rgba(168,85,247,0.07)" },
  "Quality Control": { color: "#14b8a6", bg: "rgba(20,184,166,0.07)" },
  "Dispatch":        { color: "#f59e0b", bg: "rgba(245,158,11,0.07)" },
  "Completed":       { color: "#94a3b8", bg: "rgba(148,163,184,0.07)" },
  "Stores":          { color: "#f97316", bg: "rgba(249,115,22,0.07)" },
};

export const STAGE_PATHS = {
  "PO Received": "/po-received",
  "Procurement": "/procurement",
  "Manufacturing": "/manufacturing",
  "Quality Control": "/qc-check",
  "Dispatch": "/dispatch",
  "Completed": "/completed",
  "Stores": "/stores",
};

export const PIPELINE_STAGES = ["PO Received", "Procurement", "Manufacturing", "Quality Control", "Dispatch"];
export const ALL_STAGES = ["PO Received", "Procurement", "Manufacturing", "Quality Control", "Dispatch", "Completed"];
export const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
export const PRIORITY_ORDER = { Expedite: 0, Standard: 1 };

export function formatCurrency(val) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

export function formatDateShort(str) {
  if (!str) return "";
  const d = new Date(str);
  return `${String(d.getDate()).padStart(2,"0")}-${MONTHS_SHORT[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
}

export function parseDateDash(str) {
  if (!str) return null;
  if (str.length >= 10 && str[4] === "-") return new Date(str);
  const parts = str.split("/");
  if (parts.length === 3) {
    const [d, m, y] = parts;
    const fullYear = y.length === 2 ? `20${y}` : y;
    const candidate = new Date(`${fullYear}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`);
    if (!isNaN(candidate)) return candidate;
  }
  return null;
}

// "12 Feb'26" — used in attention rows + report
export function fmtDateShort(raw) {
  if (!raw) return "—";
  const s = String(raw).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  const mo = MONTHS_SHORT[parseInt(m[2], 10) - 1] || "";
  return `${parseInt(m[3], 10)} ${mo}'${m[1].slice(2)}`;
}

export function parseIsoOrDash(str) {
  if (!str) return null;
  const s = String(str);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s);
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (!m) return null;
  const [, dd, mm, yy] = m;
  const y = yy.length === 2 ? `20${yy}` : yy;
  return new Date(`${y}-${mm.padStart(2,"0")}-${dd.padStart(2,"0")}`);
}

export function fmtAge(iso) {
  if (!iso) return "—";
  try {
    const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (d >= 1) return `${d}d ${h}h`;
    if (h >= 1) return `${h}h ${m}m`;
    return `${m}m`;
  } catch (_e) { return "—"; }
}

export function getDueDatePill(dueDateStr) {
  if (!dueDateStr) return { label: "N/A", class: "bg-zinc-500/10 text-zinc-400" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = parseDateDash(dueDateStr);
  if (!due || isNaN(due)) return { label: "N/A", class: "bg-zinc-500/10 text-zinc-400" };
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0)  return { label: `${Math.abs(diffDays)}d overdue`, class: "bg-red-500/10 text-red-400" };
  if (diffDays <= 7) return { label: `${diffDays}d left`, class: "bg-orange-500/10 text-orange-400" };
  if (diffDays <= 14)return { label: `${diffDays}d left`, class: "bg-yellow-500/10 text-yellow-400" };
  return { label: `${diffDays}d left`, class: "bg-green-500/10 text-green-400" };
}

export function daysBetween(a, b) {
  if (!a || !b) return 0;
  return Math.max(0, Math.round((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24)));
}

// Compact money formatter: $1.2M / $340K / $88  (no cents)
export function fmtMoney(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}